/**
 * Client-side state and pure helpers for the Section 8 "Create Case" wizard.
 *
 * `salary`, `approved`, and `hapStarted` are UI-only — they gate the flow and
 * derive the persisted `status`, but are never written to the database.
 */
import { emailSchema, optionalEmailError } from "@/lib/validation";

export interface WizardForm {
  tenantName: string;
  property: string; // pipeline_property_id
  salary: string; // UI-only — eligibility gate
  tenantPhone: string;
  tenantEmail: string;
  voucherAmount: string;
  phaName: string;
  phaPhone: string;
  phaEmail: string;
  phaParticipantCode: string; // set when a PHA is picked from the directory
  askingRent: string;
  paymentStandard: string;
  utilityAllowance: string;
  submissionDate: string;
  approved: "" | "yes" | "no"; // UI-only — reveals approvalDate, derives status
  approvalDate: string;
  moveInDate: string;
  hapStarted: boolean; // UI-only — reveals hapStartDate, derives status
  hapStartDate: string;
}

export const INITIAL: WizardForm = {
  tenantName: "", property: "", salary: "",
  tenantPhone: "", tenantEmail: "", voucherAmount: "",
  phaName: "", phaPhone: "", phaEmail: "", phaParticipantCode: "", askingRent: "",
  paymentStandard: "", utilityAllowance: "", submissionDate: "",
  approved: "", approvalDate: "", moveInDate: "", hapStarted: false, hapStartDate: "",
};

export type SectionKey = "eligibility" | "voucher" | "pha" | "approval" | "movein";
export type Eligibility = "idle" | "pass" | "fail";

export const STEPS: { key: SectionKey; label: string }[] = [
  { key: "eligibility", label: "Eligibility" },
  { key: "voucher", label: "Voucher" },
  { key: "pha", label: "PHA" },
  { key: "approval", label: "Approval" },
  { key: "movein", label: "Move-in" },
];

/**
 * Household size used for the HUD income-limit lookup. Hard-coded until tenant
 * household size is captured on the case — then this default is replaced with
 * the real value (the limits table has columns for sizes 1–8).
 */
export const DEFAULT_FAMILY_SIZE = 4;

/** A property the workspace lists for rent and accepts vouchers on. */
export interface Section8PropertyOption {
  value: string; // pipeline_property_id
  label: string; // address
  zip: string;
  city: string;
  state: string;
  lat?: number;
  lng?: number;
}

/**
 * Properties selectable in the wizard: a property may hold only one Section 8
 * case per workspace, so any that already have a case are hidden — except
 * `keepPropertyId` (the property of the case being edited), which stays visible.
 */
export function availableProperties(
  properties: Section8PropertyOption[],
  casedPropertyIds: Iterable<string>,
  keepPropertyId?: string,
): Section8PropertyOption[] {
  const cased = new Set(casedPropertyIds);
  return properties.filter((p) => !cased.has(p.value) || p.value === keepPropertyId);
}

/** HUD annual income limits for one HUD area at a given household size. */
export interface IncomeLimits {
  extremelyLow: number; // ~30% AMI
  veryLow: number; // ~50% AMI
  low: number; // ~80% AMI
}

export type IncomeBracket = "extremely_low" | "very_low" | "low";

export const BRACKET_LABEL: Record<IncomeBracket, string> = {
  extremely_low: "Extremely Low Income (≤30% AMI)",
  very_low: "Very Low Income (≤50% AMI)",
  low: "Low Income (≤80% AMI)",
};

/**
 * The tightest HUD bracket an annual income falls within, or null when it
 * exceeds the Low Income (80% AMI) limit — i.e. not voucher-eligible.
 */
export function classifyIncome(annualIncome: number, limits: IncomeLimits): IncomeBracket | null {
  if (annualIncome <= 0) return null;
  if (annualIncome <= limits.extremelyLow) return "extremely_low";
  if (annualIncome <= limits.veryLow) return "very_low";
  if (annualIncome <= limits.low) return "low";
  return null;
}

/** Result of the HUD income-limit eligibility lookup for one tenant + area. */
export interface EligibilityResult {
  status: Exclude<Eligibility, "idle">;
  bracket: IncomeBracket | null; // set when status === "pass"
  hudAreaName: string | null;
  annualIncome: number;
  limits: IncomeLimits | null;
  /** Why a check failed, for messaging. null on pass. */
  error: "no_zip_match" | "no_income_data" | "over_limit" | "missing_input" | null;
}

/**
 * Tenant email is optional, but any value entered must be a valid address.
 * A present, well-formed value is trimmed before it reaches the database.
 * The rule itself is shared with every other optional-email field (see
 * `@/lib/validation`) so the message stays identical across the app.
 */
export const tenantEmailSchema = emailSchema;

/** Validation message for the tenant email, or null when blank/valid. */
export const tenantEmailError = optionalEmailError;

/** Maps the UI toggles to the persisted Section8Case status. */
export function deriveStatus(form: WizardForm): string {
  if (form.hapStarted) return "leased";
  if (form.approved === "yes") return "approved";
  if (form.submissionDate) return "pha_submitted";
  return "not_started";
}
